import type { CountryObject } from '@/components/helpers/metaobjects.types'

export const LOCALIZATION_GEO_MISMATCH = 'localization:geo-mismatch'

const PERSIST_DISMISSAL = true
const DISMISSAL_KEY = 'localization-modal-dismissed'
const DISMISSAL_DAYS = 7

const CONTENT_SELECTOR = 'template[data-localization-content]'

const hasDismissalCookie = (): boolean =>
  document.cookie.split(';').some((cookie) => cookie.trim().startsWith(`${DISMISSAL_KEY}=`))

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, Math.max(ms, 0)))

const nextFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

const setDismissalCookie = () => {
  const expires = new Date(Date.now() + DISMISSAL_DAYS * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${DISMISSAL_KEY}=1; expires=${expires}; path=/; SameSite=Lax`
}

type CurrencyPart = {
  handle: string
  name?: string
  options?: Record<string, string>
}

type BrowsingContextSuggestionsResponse = {
  detected_values: {
    country: {
      handle: string
      name: string
    }
    currency?: CurrencyPart
  }
  suggestions: Array<{
    parts: {
      country?: {
        handle: string
        name: string
      }
      currency?: CurrencyPart
    }
  }>
}

/**
 * Geo localization modal. On connect it compares the visitor's IP-detected country
 * against the active market and, on a mismatch, injects its `<template>` contents
 * after `data-delay` milliseconds, then populates and reveals them with the detected
 * country's data. Emits `localization:geo-mismatch` so other UI (e.g. the country
 * selector warning) can react.
 */
export class LocalizationModalElement extends HTMLElement {
  connectedCallback() {
    this.detectAndShow().catch(() => {})
  }

  private get delay(): number {
    const delay = Number.parseInt(this.dataset.delay ?? '', 10)
    return Number.isFinite(delay) && delay > 0 ? delay : 0
  }

  private mountContent(): boolean {
    if (this.querySelector('[data-id="modal-localization"]')) return true

    const template = this.querySelector<HTMLTemplateElement>(CONTENT_SELECTOR)
    if (!template) return false

    this.appendChild(template.content.cloneNode(true))
    template.remove()

    return true
  }

  private getSuggestedCountry(response: BrowsingContextSuggestionsResponse, activeMarket: string) {
    const detected = response.detected_values?.country
    if (detected?.handle && detected.handle.toLowerCase() !== activeMarket.toLowerCase()) {
      return detected
    }

    const suggested = response.suggestions?.[0]?.parts?.country
    if (suggested?.handle && suggested.handle.toLowerCase() !== activeMarket.toLowerCase()) {
      return suggested
    }

    return null
  }

  private findMatchingCountry(handle: string): CountryObject | undefined {
    const countries = window.globals?.metaobjects?.countries || []
    return countries.find((country) => {
      if (country.country_code?.toLowerCase() === handle.toLowerCase()) return true
      const [, countryCode] = country.hreflang?.text?.split('-') || []
      return countryCode?.toLowerCase() === handle.toLowerCase()
    })
  }

  private isMarketCountry(handle: string): boolean {
    return (window.globals?.localization?.countries || []).some(
      (country) => country.iso_code?.toLowerCase() === handle.toLowerCase()
    )
  }

  private getCurrencyLabel(countryCode: string): string {
    const availableCountry = window.globals?.localization?.countries?.find(
      (country) => country.iso_code?.toLowerCase() === countryCode.toLowerCase()
    )
    const currency = availableCountry?.currency
    if (currency && (currency.symbol || currency.iso_code)) {
      return `${currency.symbol ?? ''}${currency.iso_code ?? countryCode}`
    }

    const metaobjectCurrency = this.findMatchingCountry(countryCode)?.country_currency
    if (metaobjectCurrency) return metaobjectCurrency

    return countryCode
  }

  private formatCurrencyPart(part?: CurrencyPart): string | undefined {
    if (!part?.handle) return undefined
    const optionLabel = part.options?.[part.handle]
    if (optionLabel) {
      const [iso, ...symbolParts] = optionLabel.trim().split(/\s+/)
      const symbol = symbolParts.join(' ')
      return symbol ? `${symbol}${iso}` : iso
    }
    return part.handle
  }

  private getSuggestedCurrencyLabel(response: BrowsingContextSuggestionsResponse): string | undefined {
    const suggestionCurrency = response.suggestions?.find((s) => s.parts?.currency)?.parts?.currency
    return this.formatCurrencyPart(suggestionCurrency) ?? this.formatCurrencyPart(response.detected_values?.currency)
  }

  private populate(handle: string, name: string, currencyLabelOverride?: string) {
    const matchingCountry = this.findMatchingCountry(handle)
    const switchUrl = matchingCountry?.hreflang?.url || matchingCountry?.url
    const currencyLabel = currencyLabelOverride || this.getCurrencyLabel(handle)

    this.querySelectorAll('[data-geo-localization="country-name"], [data-geo-localization="switch-name"]').forEach(
      (el) => {
        el.textContent = name
      }
    )

    const message = this.querySelector('[data-geo-localization="message"]')
    if (message) {
      message.textContent = `Switch to the ${name} site to shop in ${currencyLabel} and receive their shipping options.`
    }

    const flag = this.querySelector<HTMLImageElement>('[data-geo-localization="flag"]')
    if (flag) {
      const metaobjectFlag = matchingCountry?.country_flag
      if (typeof metaobjectFlag === 'string' && metaobjectFlag) {
        flag.src = metaobjectFlag
      } else if (metaobjectFlag && typeof metaobjectFlag === 'object' && metaobjectFlag.url) {
        flag.src = metaobjectFlag.url
      } else {
        flag.src = `//cdn.shopify.com/static/images/flags/${handle.toLowerCase()}.svg?width=48`
      }
      flag.alt = name
    }

    const switchLink = this.querySelector<HTMLAnchorElement>('[data-geo-localization="switch"]')
    if (!switchLink) return

    switchLink.href = switchUrl || '#'
    switchLink.onclick = (event) => {
      event.preventDefault()
      if (PERSIST_DISMISSAL) setDismissalCookie()

      if (switchUrl) {
        window.location.assign(switchUrl)
        return
      }

      this.submitLocalizationForm(handle)
    }
  }

  private submitLocalizationForm(handle: string) {
    const languageCode = window.globals?.localization?.language || 'en'
    const root = window.globals?.routes?.root || '/'
    const formId = `localization-update-${handle.toLowerCase()}`

    document.getElementById(formId)?.remove()

    const form = document.createElement('form')
    form.id = formId
    form.method = 'post'
    form.action = `${root}localization`
    form.acceptCharset = 'UTF-8'
    form.enctype = 'multipart/form-data'
    form.hidden = true

    const fields: Record<string, string> = {
      form_type: 'localization',
      utf8: '✓',
      _method: 'put',
      return_to: window.location.pathname + window.location.search,
      country_code: handle,
      language_code: languageCode
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  private async reveal(handle: string, name: string, currencyLabel?: string) {
    await customElements.whenDefined('x-modal')
    if (!this.mountContent()) return

    const modal = this.querySelector('[data-id="modal-localization"]') as
      | (HTMLElement & { handleShow?: () => void })
      | null
    if (!modal) return

    this.querySelectorAll('[data-modal-role~="close"]').forEach((el) =>
      el.addEventListener('click', this.handleDismiss, { once: true })
    )

    try {
      this.populate(handle, name, currencyLabel)
    } catch {
    }

    await nextFrame()

    modal.handleShow?.()
    modal.setAttribute('data-ui', 'visible')
  }

  private handleDismiss = () => {
    if (PERSIST_DISMISSAL) setDismissalCookie()
  }

  private async detectAndShow() {
    if (PERSIST_DISMISSAL && hasDismissalCookie()) return

    const startedAt = performance.now()

    const activeMarket = window.globals?.localization?.country
    if (!activeMarket) return

    const root = window.globals?.routes?.root || '/'

    let response: BrowsingContextSuggestionsResponse
    try {
      response = await fetch(
        `${root}browsing_context_suggestions.json?country[enabled]=true&currency[enabled]=true`
      ).then((r) => r.json())
    } catch {
      return
    }

    const suggested = this.getSuggestedCountry(response, activeMarket)
    if (!suggested?.handle || !suggested?.name) return

    const popupCountry = this.findMatchingCountry(suggested.handle)
    const externalUrl = popupCountry?.hreflang?.url || popupCountry?.url
    if (!externalUrl && !this.isMarketCountry(suggested.handle)) return

    const currencyLabel = this.getSuggestedCurrencyLabel(response)

    document.dispatchEvent(
      new CustomEvent(LOCALIZATION_GEO_MISMATCH, {
        detail: { handle: suggested.handle, name: suggested.name }
      })
    )

    await wait(this.delay - (performance.now() - startedAt))
    if (!this.isConnected) return

    await this.reveal(suggested.handle, suggested.name, currencyLabel)
  }
}

customElements.define('localization-modal', LocalizationModalElement)
